import React from "react";

import type { CellContext, RowData } from "@tanstack/react-table-v9";

import type { TableV2Features } from "./features";
import { formatCellValue } from "./formatters";
import StatusPill from "../StatusPill/StatusPill";
import type { StatusPillTypeProps } from "../StatusPill/StatusPill";
import SuppressedCount from "../SuppressedCount/SuppressedCount";
import TableLink from "../Table/TableLink";

type Context<TData extends RowData> = CellContext<
  TableV2Features,
  TData,
  // `any` like `TableV2ColumnDef`, so a helper fits every accessor's cell
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
>;

export interface LinkCellOptions<TData extends RowData> {
  href: (row: TData) => string | undefined;
  newTab?: boolean;
  hasPermission?: (row: TData) => boolean;
}

export const linkCell =
  <TData extends RowData>({
    href,
    newTab,
    hasPermission,
  }: LinkCellOptions<TData>) =>
  (context: Context<TData>) => {
    const meta = context.column.columnDef.meta;
    const text = formatCellValue(context.getValue(), meta);
    if (text === null) return meta?.fallback ?? "";
    const row = context.row.original;
    return (
      <TableLink
        url={href(row)}
        newTab={newTab}
        hasPermission={hasPermission ? hasPermission(row) : true}
      >
        {text}
      </TableLink>
    );
  };

/** Shows `<50` (with tooltip) when RLS suppresses the count, otherwise the column's formatted value */
export const suppressedCountCell =
  <TData extends RowData>(
    isSuppressed: (row: TData) => boolean | null | undefined,
  ) =>
  (context: Context<TData>) => {
    if (isSuppressed(context.row.original))
      return <SuppressedCount suppressed value={null} />;
    const meta = context.column.columnDef.meta;
    return formatCellValue(context.getValue(), meta) ?? meta?.fallback ?? "";
  };

export type BadgeTone = StatusPillTypeProps;

export const badgeCell =
  <TData extends RowData>(
    tones: Record<string, BadgeTone>,
    fallbackTone: BadgeTone = "default",
  ) =>
  (context: Context<TData>) => {
    const value = context.getValue();
    if (value === null || value === undefined || value === "") {
      return context.column.columnDef.meta?.fallback ?? "";
    }
    const text = String(value);
    return <StatusPill small text={text} type={tones[text] ?? fallbackTone} />;
  };

export interface ImageCellOptions<TData extends RowData> {
  src: (row: TData) => string | undefined;
  alt?: (row: TData) => string;
  size?: number;
  rounded?: boolean;
}

export const imageCell =
  <TData extends RowData>({
    src,
    alt,
    size = 32,
    rounded = false,
  }: ImageCellOptions<TData>) =>
  (context: Context<TData>) => {
    const row = context.row.original;
    const url = src(row);
    if (!url) return context.column.columnDef.meta?.fallback ?? "";
    return (
      <img
        src={url}
        alt={alt ? alt(row) : ""}
        width={size}
        height={size}
        loading="lazy"
        style={{ objectFit: "cover", borderRadius: rounded ? "50%" : 4 }}
      />
    );
  };
